import { useState, useMemo, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft } from "lucide-react";

const ZONE_METHODS = [
  { value: "sma200", label: "SMA 200", labelAr: "SMA 200" },
  { value: "macd", label: "MACD", labelAr: "MACD" },
  { value: "rsi", label: "RSI", labelAr: "RSI" },
  { value: "ichimoku", label: "Ichimoku", labelAr: "إيشيموكو" },
  { value: "supertrend", label: "Supertrend", labelAr: "سوبرترند" },
  { value: "vwap", label: "VWAP", labelAr: "VWAP" },
  { value: "alligator", label: "Alligator", labelAr: "التمساح" },
  { value: "ema_ribbon", label: "EMA Ribbon", labelAr: "شريط EMA" },
];

export function MorningStarPage({ chartData, signal, multiIndicatorData, symbol, timeframe, lang, onBack }) {
  const [method, setMethod] = useState("sma200");

  // Detect Morning Star & Evening Star patterns in chartData
  const { patternType, patternCandles, patternDetails } = useMemo(() => {
    if (!chartData || chartData.length < 5) return { patternType: null, patternCandles: [], patternDetails: null };

    // Search last 10 candles for pattern
    for (let i = chartData.length - 1; i >= Math.max(2, chartData.length - 10); i--) {
      const c1 = chartData[i - 2]; // First candle
      const c2 = chartData[i - 1]; // Middle (star)
      const c3 = chartData[i];     // Third candle
      if (!c1 || !c2 || !c3 || !c1.open || !c2.open || !c3.open) continue;

      const body1 = Math.abs(c1.close - c1.open);
      const body2 = Math.abs(c2.close - c2.open);
      const body3 = Math.abs(c3.close - c3.open);
      const range1 = c1.high - c1.low;
      const midpoint1 = (c1.open + c1.close) / 2;

      // Morning Star: bearish + small + bullish (close above midpoint of first)
      if (c1.close < c1.open && body2 < range1 * 0.4 && c3.close > c3.open && c3.close > midpoint1) {
        return {
          patternType: "MORNING_STAR",
          patternCandles: [i - 2, i - 1, i],
          patternDetails: {
            name: "Morning Star", nameAr: "نجمة الصباح",
            direction: "BUY",
            entry: c3.close,
            sl: Math.min(c1.low, c2.low, c3.low),
            strength: body3 > body1 * 0.6 ? "STRONG" : "MODERATE",
          }
        };
      }

      // Evening Star: bullish + small + bearish (close below midpoint of first)
      if (c1.close > c1.open && body2 < range1 * 0.4 && c3.close < c3.open && c3.close < midpoint1) {
        return {
          patternType: "EVENING_STAR",
          patternCandles: [i - 2, i - 1, i],
          patternDetails: {
            name: "Evening Star", nameAr: "نجمة المساء",
            direction: "SELL",
            entry: c3.close,
            sl: Math.max(c1.high, c2.high, c3.high),
            strength: body3 > body1 * 0.6 ? "STRONG" : "MODERATE",
          }
        };
      }
    }
    return { patternType: null, patternCandles: [], patternDetails: null };
  }, [chartData]);

  // Get indicator confirmations
  const confirmations = useMemo(() => {
    const indicators = multiIndicatorData?.indicators || {};
    const rsiVal = indicators.rsi?.value || 50;
    const macdSig = indicators.macd?.signal_type;
    const smaDir = indicators.sma?.signal_type;
    const sr = indicators.support_resistance;

    const confs = [];
    if (patternType === "MORNING_STAR") {
      confs.push({ name: "RSI < 30", nameAr: "RSI تشبع بيعي", met: rsiVal < 30, value: rsiVal.toFixed(1) });
      confs.push({ name: "MACD Bullish", nameAr: "MACD صعودي", met: macdSig === "BUY", value: macdSig });
      confs.push({ name: "Above MA 50", nameAr: "فوق MA 50", met: smaDir === "BUY", value: smaDir });
      confs.push({ name: "Near Support", nameAr: "قرب الدعم", met: sr && sr.distance_to_support_pct < 1, value: sr?.distance_to_support_pct?.toFixed(2) + "%" });
    } else if (patternType === "EVENING_STAR") {
      confs.push({ name: "RSI > 70", nameAr: "RSI تشبع شرائي", met: rsiVal > 70, value: rsiVal.toFixed(1) });
      confs.push({ name: "MACD Bearish", nameAr: "MACD هبوطي", met: macdSig === "SELL", value: macdSig });
      confs.push({ name: "Below MA 50", nameAr: "تحت MA 50", met: smaDir === "SELL", value: smaDir });
      confs.push({ name: "Near Resistance", nameAr: "قرب المقاومة", met: sr && sr.distance_to_resistance_pct < 1, value: sr?.distance_to_resistance_pct?.toFixed(2) + "%" });
    } else {
      // No pattern - check general conditions
      confs.push({ name: "RSI", nameAr: "RSI", met: rsiVal < 30 || rsiVal > 70, value: rsiVal.toFixed(1) });
      confs.push({ name: "MACD", nameAr: "MACD", met: macdSig === "BUY" || macdSig === "SELL", value: macdSig || "NEUTRAL" });
      confs.push({ name: "SMA", nameAr: "SMA", met: smaDir === "BUY" || smaDir === "SELL", value: smaDir || "NEUTRAL" });
      confs.push({ name: "S/R", nameAr: "دعم/مقاومة", met: sr && (sr.distance_to_support_pct < 1 || sr.distance_to_resistance_pct < 1), value: "—" });
    }
    return confs;
  }, [multiIndicatorData, patternType]);

  // Calculate Entry/SL/TP based on pattern
  const tradeSetup = useMemo(() => {
    if (!patternDetails || !chartData || chartData.length === 0) return null;
    const lastCandle = chartData[chartData.length - 1];
    const atr = lastCandle?.atr || (lastCandle?.close * 0.002);
    const entry = patternDetails.entry;
    
    if (patternDetails.direction === "BUY") {
      const sl = patternDetails.sl - atr * 0.3;
      const risk = entry - sl;
      return { entry, sl, tp1: entry + risk * 2, tp2: entry + risk * 3, direction: "BUY", risk };
    } else {
      const sl = patternDetails.sl + atr * 0.3;
      const risk = sl - entry;
      return { entry, sl, tp1: entry - risk * 2, tp2: entry - risk * 3, direction: "SELL", risk };
    }
  }, [patternDetails, chartData]);

  // Zone logic for background
  const processedData = useMemo(() => {
    if (!chartData || chartData.length === 0) return [];
    return chartData.map((d) => {
      let isBuyZone = false;
      if (method === "sma200") isBuyZone = d.close > (d.sma_50 || d.sma_20 || d.close);
      else if (method === "macd") isBuyZone = (d.macd || 0) > (d.macd_signal || 0);
      else if (method === "rsi") isBuyZone = (d.rsi || 50) > 50;
      else if (method === "ichimoku") isBuyZone = d.close > Math.max(d.ichimoku_senkou_a || 0, d.ichimoku_senkou_b || 0);
      else if (method === "supertrend") isBuyZone = d.close > ((d.sma_9 || d.sma_20 || d.close) - (d.atr || 0) * 1.5);
      else if (method === "vwap") isBuyZone = d.close > (d.sma_20 || d.close);
      else if (method === "alligator") isBuyZone = d.close > (d.sma_50 || d.sma_20 || d.close) && (d.sma_9 || 0) > (d.sma_20 || 0);
      else if (method === "ema_ribbon") isBuyZone = (d.ema_12 || 0) > (d.ema_26 || 0);
      return { ...d, zone: isBuyZone ? "BUY" : "SELL" };
    });
  }, [chartData, method]);

  // Price scale
  const { priceMin, priceMax } = useMemo(() => {
    if (processedData.length === 0) return { priceMin: 0, priceMax: 0 };
    const closes = processedData.map(d => d.close).filter(c => c > 0);
    const avgClose = closes.reduce((a, b) => a + b, 0) / closes.length;
    const threshold = avgClose * 0.5;
    let min = Infinity, max = -Infinity;
    processedData.forEach(d => {
      const lo = d.low > threshold ? d.low : d.close;
      const hi = d.high > threshold ? d.high : d.close;
      if (lo < min) min = lo;
      if (hi > max) max = hi;
    });
    if (min === Infinity) { min = avgClose * 0.99; max = avgClose * 1.01; }
    const pad = (max - min) * 0.06;
    return { priceMin: min - pad, priceMax: max + pad };
  }, [processedData]);

  const chartW = 1500, chartH = 700;
  const mL = 5, mR = 5, mT = 5, mB = 5;
  const dW = chartW - mL - mR, dH = chartH - mT - mB;

  const scaleY = useCallback((val) => {
    const range = priceMax - priceMin;
    if (range === 0) return mT + dH / 2;
    return mT + dH - ((val - priceMin) / range) * dH;
  }, [priceMin, priceMax, dH]);

  if (!chartData || chartData.length === 0) return null;

  const gap = dW / processedData.length;
  const candleW = Math.max(6, Math.min(18, gap * 0.8));
  const closes = processedData.map(d => d.close).filter(c => c > 0);
  const avgC = closes.reduce((a, b) => a + b, 0) / closes.length;
  const thr = avgC * 0.5;

  const getZoneLine = (d) => {
    if (method === "sma200") return d.sma_50 || d.sma_20;
    if (method === "vwap") return d.sma_20;
    if (method === "supertrend") return (d.sma_9 || d.sma_20 || d.close) - (d.atr || 0) * 1.5;
    if (method === "ema_ribbon") return d.ema_26;
    if (method === "alligator") return d.sma_50 || d.sma_20;
    return null;
  };

  const metCount = confirmations.filter(c => c.met).length;
  const signalStrength = patternDetails ? (metCount >= 3 ? 95 : metCount >= 2 ? 75 : metCount >= 1 ? 55 : 30) : 0;

  return (
    <div className="min-h-screen bg-zinc-950 p-3" data-testid="morning-star-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5 text-zinc-300" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-yellow-400">
              {lang === "ar" ? "نجمة الصباح / نجمة المساء" : "Morning Star / Evening Star"}
            </h1>
            <p className="text-xs text-zinc-400">{symbol} - {timeframe}</p>
          </div>
        </div>
        {/* Pattern Status */}
        {patternDetails ? (
          <Badge className={`text-sm px-3 py-1 ${patternDetails.direction === "BUY" ? "bg-green-600" : "bg-red-600"}`}>
            {lang === "ar" ? patternDetails.nameAr : patternDetails.name} ({patternDetails.strength})
          </Badge>
        ) : (
          <Badge className="text-sm px-3 py-1 bg-zinc-700">
            {lang === "ar" ? "لا نموذج حالياً" : "No Pattern Detected"}
          </Badge>
        )}
      </div>

      {/* Method Selector */}
      <div className="flex gap-1.5 mb-3 overflow-x-auto pb-1">
        {ZONE_METHODS.map((m) => (
          <button key={m.value} onClick={() => setMethod(m.value)}
            className={`px-3 py-1.5 text-xs rounded-lg whitespace-nowrap ${method === m.value ? "bg-blue-600 text-white" : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"}`}
            data-testid={`zone-method-${m.value}`}>{lang === "ar" ? m.labelAr : m.label}</button>
        ))}
      </div>

      {/* CHART - CLEAN, BIG, NO TEXT ON IT */}
      <Card className="bg-zinc-900 border-zinc-800 mb-3">
        <CardContent className="p-1">
          <svg width="100%" viewBox={`0 0 ${chartW} ${chartH}`} className="w-full" style={{ minHeight: "60vh" }}>
            <rect x={0} y={0} width={chartW} height={chartH} fill="#0a0a0a" />

            {/* Subtle grid */}
            {[0.25, 0.5, 0.75].map((pct, i) => (
              <line key={i} x1={0} y1={mT + dH * pct} x2={chartW} y2={mT + dH * pct} stroke="#111" />
            ))}

            {/* Zone backgrounds */}
            {processedData.map((d, i) => (
              <rect key={`z-${i}`} x={mL + i * gap} y={mT} width={gap} height={dH}
                fill={d.zone === "BUY" ? "rgba(34,197,94,0.03)" : "rgba(239,68,68,0.03)"} />
            ))}

            {/* Zone Line - PINK */}
            {(method !== "macd" && method !== "rsi" && method !== "ichimoku") && (
              <polyline fill="none" stroke="#ec4899" strokeWidth="3"
                points={processedData.map((d, i) => {
                  const v = getZoneLine(d);
                  return v ? `${mL + i * gap + gap / 2},${scaleY(v)}` : null;
                }).filter(Boolean).join(' ')} />
            )}

            {/* Expert Lines */}
            <polyline fill="none" stroke="#22d3ee" strokeWidth="2.5"
              points={processedData.map((d, i) => d.ema_12 ? `${mL + i * gap + gap / 2},${scaleY(d.ema_12)}` : null).filter(Boolean).join(' ')} />
            <polyline fill="none" stroke="#fb923c" strokeWidth="2.5"
              points={processedData.map((d, i) => d.ema_26 ? `${mL + i * gap + gap / 2},${scaleY(d.ema_26)}` : null).filter(Boolean).join(' ')} />
            <polyline fill="none" stroke="#ffffff" strokeWidth="2"
              points={processedData.map((d, i) => d.sma_50 ? `${mL + i * gap + gap / 2},${scaleY(d.sma_50)}` : null).filter(Boolean).join(' ')} />

            {/* Alligator extra lines */}
            {method === "alligator" && (
              <>
                <polyline fill="none" stroke="#3b82f6" strokeWidth="2.5"
                  points={processedData.map((d, i) => d.sma_50 ? `${mL + i * gap + gap / 2},${scaleY(d.sma_50)}` : null).filter(Boolean).join(' ')} />
                <polyline fill="none" stroke="#22c55e" strokeWidth="2"
                  points={processedData.map((d, i) => d.sma_9 ? `${mL + i * gap + gap / 2},${scaleY(d.sma_9)}` : null).filter(Boolean).join(' ')} />
              </>
            )}

            {/* Ichimoku cloud */}
            {method === "ichimoku" && processedData.map((d, i) => {
              const sa = d.ichimoku_senkou_a, sb = d.ichimoku_senkou_b;
              if (!sa || !sb) return null;
              const y1 = scaleY(Math.max(sa, sb)), y2 = scaleY(Math.min(sa, sb));
              return <rect key={`cl-${i}`} x={mL + i * gap} y={y1} width={gap} height={y2 - y1} fill={sa > sb ? "rgba(34,197,94,0.08)" : "rgba(239,68,68,0.08)"} />;
            })}

            {/* Candlesticks */}
            {processedData.map((d, i) => {
              if (!d.open || !d.close || !d.high || !d.low || d.low < thr) return null;
              const x = mL + i * gap + gap / 2;
              const bull = d.close >= d.open;
              const isPatternCandle = patternCandles.includes(i);
              const color = isPatternCandle ? "#fbbf24" : (bull ? "#22c55e" : "#ef4444");
              const strokeC = isPatternCandle ? "#f59e0b" : (bull ? "#16a34a" : "#dc2626");
              const bTop = scaleY(Math.max(d.open, d.close));
              const bBot = scaleY(Math.min(d.open, d.close));
              const bH = Math.max(2, bBot - bTop);
              const w = isPatternCandle ? candleW * 1.2 : candleW;
              return (
                <g key={`c-${i}`}>
                  <line x1={x} y1={scaleY(d.high)} x2={x} y2={scaleY(d.low)} stroke={strokeC} strokeWidth={isPatternCandle ? "2" : "1.2"} />
                  <rect x={x - w / 2} y={bTop} width={w} height={bH} fill={color} stroke={strokeC} strokeWidth="0.5" rx="0.5" />
                </g>
              );
            })}

            {/* Entry/SL/TP lines - thin, no labels */}
            {tradeSetup && (
              <>
                <line x1={0} y1={scaleY(tradeSetup.entry)} x2={chartW} y2={scaleY(tradeSetup.entry)} stroke="#eab308" strokeWidth="1.5" strokeDasharray="8 4" />
                <line x1={0} y1={scaleY(tradeSetup.sl)} x2={chartW} y2={scaleY(tradeSetup.sl)} stroke="#ef4444" strokeWidth="1.5" strokeDasharray="5 4" />
                <line x1={0} y1={scaleY(tradeSetup.tp1)} x2={chartW} y2={scaleY(tradeSetup.tp1)} stroke="#22c55e" strokeWidth="1.5" strokeDasharray="5 4" />
                <line x1={0} y1={scaleY(tradeSetup.tp2)} x2={chartW} y2={scaleY(tradeSetup.tp2)} stroke="#34d399" strokeWidth="1" strokeDasharray="4 3" />
              </>
            )}
          </svg>
        </CardContent>
      </Card>

      {/* INFO BELOW CHART */}
      {/* Pattern Detection Result */}
      <Card className={`mb-3 border ${patternType === "MORNING_STAR" ? "border-green-700 bg-green-950/20" : patternType === "EVENING_STAR" ? "border-red-700 bg-red-950/20" : "border-zinc-800 bg-zinc-900"}`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-bold text-white">
                {patternDetails ? (lang === "ar" ? patternDetails.nameAr : patternDetails.name) : (lang === "ar" ? "لا يوجد نموذج" : "No Pattern")}
              </p>
              <p className="text-xs text-zinc-400">
                {patternDetails
                  ? (lang === "ar" ? "النموذج مكتشف - 3 شموع محددة بالأصفر على الشارت" : "Pattern detected - 3 candles highlighted in yellow")
                  : (lang === "ar" ? "انتظر ظهور النموذج" : "Waiting for pattern formation")}
              </p>
            </div>
            {patternDetails && (
              <div className="text-right">
                <p className={`text-2xl font-bold ${patternDetails.direction === "BUY" ? "text-green-400" : "text-red-400"}`}>
                  {patternDetails.direction}
                </p>
                <p className="text-xs text-zinc-400">{signalStrength}% {lang === "ar" ? "ثقة" : "confidence"}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Trade Setup + Confirmations */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        {/* Entry/SL/TP */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4">
            <p className="text-xs text-zinc-400 uppercase mb-3">{lang === "ar" ? "نقاط الدخول والخروج" : "Trade Setup"}</p>
            {tradeSetup ? (
              <div className="grid grid-cols-2 gap-2">
                <div className="p-2 rounded bg-yellow-950/30 border border-yellow-700/50">
                  <p className="text-[10px] text-yellow-400">Entry</p>
                  <p className="text-sm font-mono font-bold text-yellow-300">{tradeSetup.entry.toFixed(5)}</p>
                </div>
                <div className="p-2 rounded bg-red-950/30 border border-red-700/50">
                  <p className="text-[10px] text-red-400">Stop Loss</p>
                  <p className="text-sm font-mono font-bold text-red-300">{tradeSetup.sl.toFixed(5)}</p>
                </div>
                <div className="p-2 rounded bg-green-950/30 border border-green-700/50">
                  <p className="text-[10px] text-green-400">TP 1 (1:2)</p>
                  <p className="text-sm font-mono font-bold text-green-300">{tradeSetup.tp1.toFixed(5)}</p>
                </div>
                <div className="p-2 rounded bg-emerald-950/30 border border-emerald-700/50">
                  <p className="text-[10px] text-emerald-400">TP 2 (1:3)</p>
                  <p className="text-sm font-mono font-bold text-emerald-300">{tradeSetup.tp2.toFixed(5)}</p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-zinc-500 text-center py-4">{lang === "ar" ? "انتظر النموذج للحصول على نقاط الدخول" : "Wait for pattern to get trade levels"}</p>
            )}
          </CardContent>
        </Card>

        {/* Confirmations */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4">
            <p className="text-xs text-zinc-400 uppercase mb-3">{lang === "ar" ? "تأكيدات المؤشرات" : "Indicator Confirmations"} ({metCount}/4)</p>
            <div className="space-y-2">
              {confirmations.map((c, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="text-sm text-zinc-300">{lang === "ar" ? c.nameAr : c.name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-zinc-500">{c.value}</span>
                    <span className={`w-3 h-3 rounded-full ${c.met ? "bg-green-500" : "bg-zinc-600"}`} />
                  </div>
                </div>
              ))}
            </div>
            <div className={`mt-3 pt-3 border-t text-center p-2 rounded ${metCount >= 3 ? "border-green-700 bg-green-950/30" : metCount >= 2 ? "border-yellow-700 bg-yellow-950/30" : "border-zinc-700 bg-zinc-800"}`}>
              <p className="text-xs text-zinc-400">
                {metCount >= 3 ? (lang === "ar" ? "إشارة قوية - ادخل" : "Strong - Enter") :
                 metCount >= 2 ? (lang === "ar" ? "مقبولة - ادخل بحذر" : "OK - Enter with caution") :
                 (lang === "ar" ? "ضعيفة - لا تدخل" : "Weak - Don't enter")}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Legend */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-3">
          <div className="flex flex-wrap gap-4 text-xs text-zinc-400">
            <span className="flex items-center gap-1.5"><span className="w-4 h-1 bg-pink-500 rounded" />{lang === "ar" ? "خط الفصل" : "Zone Line"}</span>
            <span className="flex items-center gap-1.5"><span className="w-4 h-1 bg-cyan-400 rounded" />EMA 12</span>
            <span className="flex items-center gap-1.5"><span className="w-4 h-1 bg-orange-400 rounded" />EMA 26</span>
            <span className="flex items-center gap-1.5"><span className="w-4 h-1 bg-white rounded" />SMA 50</span>
            <span className="flex items-center gap-1.5"><span className="w-4 h-1 bg-yellow-400 rounded" />Entry</span>
            <span className="flex items-center gap-1.5"><span className="w-4 h-1 bg-red-500 rounded" />SL</span>
            <span className="flex items-center gap-1.5"><span className="w-4 h-1 bg-green-500 rounded" />TP</span>
            <span className="flex items-center gap-1.5"><span className="w-4 h-3 bg-yellow-500/50 rounded" />{lang === "ar" ? "شموع النموذج" : "Pattern Candles"}</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
